import { ref, type Ref } from 'vue'
import { message } from 'ant-design-vue'
import { encodeLngLat } from './utils'

/**
 * 标记点管理 Hook
 * 负责地图上的标记点添加、清除和地理编码功能
 *
 * @param Map - 地图实例的 ref 引用
 * @returns 标记点相关的方法和状态
 */
export function useTMapMarker(Map: Ref<any>) {
  const T = window.T
  const GeoCoder = new T.Geocoder()
  const loading = ref(false)

  /**
   * 在地图上添加标记点
   * 会先清除所有现有覆盖物，然后在指定位置添加新标记
   *
   * @param lnglat - 经纬度坐标 [经度, 纬度]
   * @param zoomLevel - 缩放级别，默认为 18
   */
  const addMarker = (lnglat: LngLat, zoomLevel = 18) => {
    if (!Map.value) return

    Map.value.clearOverLays()
    const point = new T.LngLat(...lnglat)
    const marker = new T.Marker(point)
    Map.value.addOverLay(marker)
    Map.value.centerAndZoom(point, zoomLevel)
  }

  /**
   * 清除地图上所有覆盖物
   */
  const clearMarker = () => {
    if (!Map.value) return
    Map.value.clearOverLays()
  }

  /**
   * 逆地理编码：根据经纬度获取地址信息
   *
   * @param lnglat - 经纬度坐标 [经度, 纬度]
   * @returns Promise<string> 返回地址字符串
   */
  const getAddressByLngLat = (lnglat: LngLat): Promise<string> => {
    return new Promise((resolve, reject) => {
      GeoCoder.getLocation(new T.LngLat(...lnglat), (rs: any) => {
        try {
          const address = rs.getAddress()
          resolve(address)
        } catch (error) {
          reject(error)
        }
      })
    })
  }

  /**
   * 更新标记点位置并获取对应地址
   * 包含加载状态管理和错误处理
   *
   * @param lnglat - 经纬度坐标 [经度, 纬度]
   * @param onSuccess - 成功回调，返回经纬度字符串和地址
   */
  const updateMarkerWithAddress = async (lnglat: LngLat, onSuccess?: (lnglatStr: string, address: string) => void) => {
    loading.value = true
    try {
      const lnglatStr = encodeLngLat(lnglat)
      const address = await getAddressByLngLat(lnglat)
      onSuccess?.(lnglatStr, address)
    } catch (err) {
      console.error('获取地址信息失败:', err)
      message.error('获取地址信息失败')
    } finally {
      loading.value = false
    }
  }

  /**
   * 地图居中到指定位置
   *
   * @param lnglat - 经纬度坐标 [经度, 纬度]
   * @param zoomLevel - 缩放级别，默认为 18
   */
  const centerToPoint = (lnglat: LngLat, zoomLevel = 18) => {
    if (!Map.value) return
    const point = new T.LngLat(...lnglat)
    Map.value.centerAndZoom(point, zoomLevel)
  }

  return {
    loading,
    addMarker,
    clearMarker,
    getAddressByLngLat,
    updateMarkerWithAddress,
    centerToPoint
  }
}
